import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import RichTextEditor from "@/components/RichTextEditor";

const CATEGORIES = ["Tea Culture", "Wellness", "Brewing Guides", "B2B Guides", "News"];
const EMPTY = { title: "", excerpt: "", content: "", category: "Tea Culture", featured_image: "", seo_title: "", seo_description: "", status: "published", author: "Leaf Alchemy" };

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); };

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    try {
      if (editing) await api.put(`/admin/blog/${editing.id}`, form);
      else await api.post("/admin/blog", form);
      toast.success(editing ? "Post updated" : "Post created");
      setOpen(false);
      load();
    } catch {
      toast.error("Failed to save");
    }
  };

  const del = async (id) => {
    await api.delete(`/admin/blog/${id}`);
    toast.success("Post deleted");
    load();
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-forest">Blog Management</h1>
        <Button onClick={openNew} data-testid="new-post-btn" className="rounded-full bg-forest hover:bg-olive text-cream transition-colors"><Plus size={16} className="mr-2" /> New Post</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {posts.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-forest/10 overflow-hidden" data-testid={`admin-post-${p.id}`}>
            <div className="h-32 overflow-hidden bg-cream">{p.featured_image && <img src={p.featured_image} alt="" className="w-full h-full object-cover" />}</div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="border-olive/40 text-olive text-xs">{p.category}</Badge>
                <Badge className={`text-xs ${p.status === "published" ? "bg-olive text-white" : "bg-gray-200 text-gray-700"}`}>{p.status}</Badge>
              </div>
              <h3 className="font-display text-lg text-forest leading-snug line-clamp-2">{p.title}</h3>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-post-${p.id}`} className="rounded-full border-forest/20 text-forest"><Pencil size={14} className="mr-1" /> Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => del(p.id)} data-testid={`delete-post-${p.id}`} className="text-red-600"><Trash2 size={14} /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="post-editor-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-forest">{editing ? "Edit Post" : "New Post"}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">{editing ? "Update this article." : "Write and publish a new blog article."}</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-forest">Title</Label><Input value={form.title} onChange={set("title")} data-testid="post-title" className="border-forest/15" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-forest">Category</Label>
                <Select value={form.category} onValueChange={set("category")}>
                  <SelectTrigger data-testid="post-category" className="border-forest/15"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-forest">Status</Label>
                <Select value={form.status} onValueChange={set("status")}>
                  <SelectTrigger data-testid="post-status" className="border-forest/15"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="published">Published</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-forest">Featured Image URL</Label><Input value={form.featured_image} onChange={set("featured_image")} data-testid="post-image" className="border-forest/15" placeholder="https://..." /></div>
            <div className="space-y-1.5"><Label className="text-forest">Excerpt</Label><Textarea rows={2} value={form.excerpt} onChange={set("excerpt")} data-testid="post-excerpt" className="border-forest/15" /></div>
            <div className="space-y-1.5"><Label className="text-forest">Content</Label>
              <RichTextEditor key={editing?.id || "new"} value={form.content} onChange={(html) => setForm((f) => ({ ...f, content: html }))} />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1.5"><Label className="text-forest">SEO Description</Label><Input value={form.seo_description} onChange={set("seo_description")} data-testid="post-seo" className="border-forest/15" /></div>
            </div>
            <Button onClick={save} data-testid="save-post" className="w-full rounded-full bg-forest hover:bg-olive text-cream transition-colors">{editing ? "Update Post" : "Create Post"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
